import './../../css/core/ListInput.css';

import { Form, Input, InputNumber, Modal, Select, Tag, Tooltip } from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import React from 'react';
import { capitalize } from 'lodash';
import { isNullOrEmpty } from './../../utils/booleanUtils';
import { nullThrows } from './../../utils/nullUtils';

const REQUIRED_RULE = { message: '"${label}" is required', required: true };

type TValue = number | string;

type TOption = {
  label: string;
  value: TValue;
};

type Props = {
  onChange: (values: Array<TValue>) => void;
  options?: TOption[];
  shouldAllowDuplicates?: boolean;
  type: 'number' | 'text';
  value?: Array<TValue>;
  valueName?: string;
};

export default function ListInput({
  onChange,
  options,
  shouldAllowDuplicates = false,
  type,
  value,
  valueName = 'value',
}: Props): React.ReactElement {
  const [form] = Form.useForm();
  const [isInputModalShown, setIsInputModalShown] = React.useState<boolean>(false);
  const [newValue, setNewValue] = React.useState<TValue | null>();
  const nonNullValue = React.useMemo(() => value ?? [], [value]);

  const onClose = React.useCallback(() => {
    setIsInputModalShown(false);
    setNewValue(null);
    form.resetFields();
  }, [form]);
  // Use index to remove values to handle for allowing duplicates
  const onRemove = React.useCallback(
    (index: number) => {
      onChange(nonNullValue.filter((_value, i) => i !== index));
    },
    [onChange, nonNullValue],
  );

  const onSubmit = React.useCallback(() => {
    if (isNullOrEmpty(newValue)) {
      return;
    }

    const nonnullNewValue = nullThrows(newValue);

    if (shouldAllowDuplicates || !nonNullValue.includes(nonnullNewValue)) {
      onChange([...nonNullValue, nonnullNewValue]);
    }

    onClose();
  }, [newValue, onClose, onChange, shouldAllowDuplicates, nonNullValue]);

  return (
    <div className="listInputRoot">
      {nonNullValue.map((value, i) => (
        <Tooltip title={value} key={`${value}-${i}`}>
          <Tag className="listInputValue" closable={true} onClose={() => onRemove(i)}>
            {value}
          </Tag>
        </Tooltip>
      ))}
      <Tag className="listInputNewValue" onClick={() => setIsInputModalShown(true)}>
        <PlusOutlined /> New {valueName}
      </Tag>
      <Modal
        onOk={onSubmit}
        onCancel={onClose}
        title={`Add ${valueName}`}
        open={isInputModalShown}
        width={900}
      >
        <Form form={form}>
          <Form.Item label={capitalize(valueName)} name="value" rules={[REQUIRED_RULE]}>
            {options != null && (
              <Select onChange={setNewValue} value={newValue}>
                {options.map(({ label, value }) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            )}
            {options == null && type === 'number' && (
              <InputNumber onChange={(value) => setNewValue(value)} value={newValue} />
            )}
            {options == null && type === 'text' && (
              <Input onChange={(e) => setNewValue(e.target.value)} value={newValue ?? ''} />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
